import { yupResolver } from '@hookform/resolvers/yup'
import { find, forEach, map } from 'lodash-es'
import { FC, useCallback, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Group } from './Group'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import {
  CurrencyAlignment,
  IGroup,
  PaymentProvider,
} from '../../../common/models/groups'
import { getGroups, getPaymentProviders } from '../../../core/services/groups'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

type Props = {
  currencies: Currency[]
}

export type TGroup = {
  id?: string
  name: string
  currencies?: TCurrency[]
  festivals?: TFestival[]
  saved?: boolean
}

export type TCurrency = {
  id?: string
  name: string
  symbol: string
  alignment: CurrencyAlignment
  active?: boolean
  main?: boolean
  conversionRate?: number
  saved?: boolean
}

export type FestivalCurrency = {
  id?: string
  name: string
  symbol?: string
  alignment: CurrencyAlignment
  active?: boolean
  main?: boolean
  conversionRate?: number
  saved?: boolean
}

export type PAYMENT_PROVIDER = 'BT_IPAY' | 'STRIPE'

export type TFestivalOrderTypes = 'TICKETS' | 'CHECKIN' | 'TRANSFER' | 'TOPUP'

export type TFestivalPaymentProvider = {
  paymentProviderId: string
  orderTypes?: TFestivalOrderTypes[]
  saved?: boolean
}

export type TFestival = {
  id: string
  defaultPaymentProviderId: string
  festivalPaymentProviderDtos?: TFestivalPaymentProvider[]
  apiSecret?: string
  username?: string
  password?: string
  currencies?: FestivalCurrency[]
  saved?: boolean
}

export type TFormValues = {
  groups?: TGroup[]
}

export const GroupsTab: FC<Props> = ({ currencies }): JSX.Element => {
  const [loading, setLoading] = useState(true)

  const [groups, setGroups] = useState<IGroup[]>([])
  const [paymentProviders, setPaymentProviders] = useState<PaymentProvider[]>(
    [],
  )

  const schema = yup.object().shape({
    groups: yup.array().of(
      yup.object().shape({
        id: yup.string().optional(),
        name: yup.string().required('Group name is required'),
        currencies: yup.array().of(
          yup.object().shape({
            id: yup.string().optional(),
            name: yup.string().required('Currency name is required'),
            symbol: yup.string().required('Currency symbol is required'),
            alignment: yup
              .string()
              .oneOf(['BEFORE_PRICE', 'AFTER_PRICE'])
              .required('Currency alignment is required'),
            active: yup.boolean().optional(),
            main: yup.boolean().optional(),
            conversionRate: yup
              .number()
              .optional()
              .transform((value) => {
                return isNaN(value) ? undefined : value
              }),
            saved: yup.boolean().optional(),
          }),
        ),
        festivals: yup.array().of(
          yup.object().shape({
            id: yup.string().required('Festival id is required'),
            currencies: yup.array().of(
              yup.object().shape({
                id: yup.string().required('Currency id is required'),
                name: yup.string().required('Currency name is required'),
                symbol: yup.string().optional(),
                alignment: yup
                  .string()
                  .oneOf(['BEFORE_PRICE', 'AFTER_PRICE'])
                  .required('Currency alignment is required'),
                active: yup.boolean().optional(),
                main: yup.boolean().optional(),
              }),
            ),
            defaultPaymentProviderId: yup.string().required(),
            festivalPaymentProviderDtos: yup.array().of(
              yup.object().shape({
                paymentProviderId: yup
                  .string()
                  .required('Payment provider id is required'),
                orderTypes: yup
                  .array()
                  .of(
                    yup
                      .string()
                      .oneOf(['TICKETS', 'CHECKIN', 'TRANSFER', 'TOPUP'])
                      .required('Order type is required'),
                  ),
              }),
            ),
            saved: yup.boolean().optional(),
          }),
        ),
        saved: yup.boolean().optional(),
      }),
    ),
  })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const [groupsResponseRes, paymentProvidersRes] = await Promise.all([
        getGroups(),
        getPaymentProviders(),
      ])
      if (
        groupsResponseRes.status === 200 &&
        paymentProvidersRes.status === 200
      ) {
        const { data: paymentProviders } = paymentProvidersRes
        setPaymentProviders(paymentProviders)
        const { data } = groupsResponseRes
        setGroups(data)
        forEach(data, (group) => {
          const groupCurrencies = map(group.currencies, (currency) => {
            return {
              id: currency.id,
              name: currency.name,
              symbol: currency.symbol || '',
              alignment: currency.symbolAlignment,
              active: find(group.currenciesDetails, { id: currency.id })
                ?.active,
              main: find(group.currenciesDetails, { id: currency.id })?.default,
              conversionRate: find(group.currenciesDetails, { id: currency.id })
                ?.exchangeRate,
              saved: true,
            }
          })
          const groupFestivals = map(group.festivals, (festival) => {
            return {
              id: festival.festivalId,
              paymentProvider: festival.paymentProviderName,
              apiSecret: festival.paymentProviderSecret,
              username: festival.paymentProviderUsername,
              password: festival.paymentProviderPassword,
              defaultPaymentProviderId: festival.defaultPaymentProviderId,
              festivalPaymentProviderDtos: festival.festivalPaymentProviderDtos,
              currencies: map(festival.currencies, (currency) => {
                return {
                  id: currency.id,
                  name: currency.name,
                  symbol: '',
                  alignment: 'BEFORE_PRICE' as CurrencyAlignment,
                  active: currency.active,
                  default: currency.default,
                  main: currency.default,
                }
              }),
              saved: true,
            }
          })
          append({
            name: group.name,
            id: group.id,
            currencies: groupCurrencies,
            festivals: groupFestivals,
            saved: true,
          })
        })
        append({
          name: '',
          currencies: [],
          festivals: [],
        })
        remove(data.length)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<TFormValues>({
    mode: 'onChange',
    resolver: yupResolver<TFormValues>(schema),
  })

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'groups',
  })

  const submit = (data: TFormValues) => {
    console.log(data)
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 space-y-4">
        {loading && <Loader />}
        <form id="groups-form" onSubmit={handleSubmit(submit)}>
          {map(fields, (field, index) => (
            <Group
              errors={errors}
              register={register}
              control={control}
              group={field}
              key={field.id}
              currencies={currencies}
              index={index}
              remove={remove}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              groups={groups}
              paymentProviders={paymentProviders}
            />
          ))}
        </form>
      </Card>
      <div className="w-full flex items-center justify-center mt-4">
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            append({
              name: '',
              currencies: [],
            })
          }}
        >
          Add new Group
          <Icon
            icon="plus"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
        </Button>
      </div>
    </>
  )
}
